import { useTheme } from '@mui/material';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { ImageGalleryFragmentFragmentDoc } from '@packages/gql/generated/shopping/ImageGalleryFragmentFragmentDoc';
import type { CarouselThumbnailConfig } from '@packages/shared';
import { Box, Carousel, DynamicYield, ErrorBoundary, Image, useDeviceType } from '@packages/shared';
import { Enlarge32 } from '@packages/themes/icons';
import { useConfig } from '@packages/utilities';
import Head from 'next/head';
import type { ReactNode } from 'react';
import { forwardRef, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useResponsiveAkamaiTemplate } from '@packages/modules/src/hooks/useResponsiveAkamaiTemplate/useResponsiveAkamaiTemplate';
import { templateNames } from '@packages/modules/src/hooks/useResponsiveAkamaiTemplate/config';
import { Cliplister } from '../Cliplister';
import { ImageGalleryCallToActionButton } from '../ImageGalleryCallToActionButton';
import { Mazing } from '../Mazing';
import { ZoomDialog } from '../ZoomDialog';
import { ImageGalleryActionsWrapper } from './ImageGalleryActionsWrapper';
import { ImageGalleryFlags } from './ImageGalleryFlags';
import { ImageGalleryThumbnail } from './ImageGalleryThumbnail';
import { imageGalleryMessages } from './messages';
import { ErrorFallback } from '../ZoomDialog/ErrorFallback';

// TODO(api-layer): add alt field to media type
/* GraphQL */ `
  fragment ImageGalleryFragment on Product {
    name
    mazingUrl
    orderNumber
    additionalFlags {
      type
      text
      src
    }
    cliplister(lang: $lang) @skip(if: $skipCliplister) {
      url
    }
    media {
      type
      url
    }
    ...CliplisterFragment
    ...MazingFragment
    ...LightboxFragment
  }
`;

type ImageGalleryProps = {
  data: FragmentType<typeof ImageGalleryFragmentFragmentDoc>;
  colorPicker?: ReactNode;
};

const thumbnailConfig: CarouselThumbnailConfig = {
  visibleSlides: 'auto',
  gap: 1.5,
  thumbnails: [],
  thumbnailBoxStyles: (isActiveSlide) => ({
    opacity: 1,
    border: `2px solid transparent`,
    borderColor: isActiveSlide ? 'text.dark' : 'transparent',
    borderRadius: '4px',
    position: 'relative',
    ...(isActiveSlide && {
      '&:after': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        border: '1px solid white',
      },
    }),
  }),
};

/**
 * ImageGallery on detailview
 * */
export const ImageGallery = forwardRef<HTMLDivElement, ImageGalleryProps>(
  ({ data, colorPicker }, ref) => {
    const product = getFragmentData(ImageGalleryFragmentFragmentDoc, data);

    const { media, additionalFlags, name, mazingUrl } = product;
    const { isMobile } = useDeviceType();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [zoomDialogOpen, setZoomDialogOpen] = useState(false);
    const parentRef = useRef<HTMLDivElement>(null);
    const { templateName: galleryTemplateName } = useResponsiveAkamaiTemplate('gallery');
    const {
      dynamicYieldEnabled,
      product: {
        mazing: { enabled: mazingEnabled },
      },
    } = useConfig();
    const theme = useTheme();
    const { formatMessage } = useIntl();

    thumbnailConfig.thumbnails = media.map(({ url }) => (
      <ImageGalleryThumbnail imgSrc={url!} alt={name} key={url} />
    ));

    const handleImageClick = () => {
      setZoomDialogOpen(true);
    };
    const handleZoomDialogClose = () => {
      setZoomDialogOpen(false);
    };

    return (
      <>
        {/* set header preload image of main image to boost LCP */}
        <Head>
          <link
            rel="preload"
            as="image"
            href={`${media[0].url}?${templateNames.dvMobile}`}
            media={`(max-width: ${theme.breakpoints.values.md}px)`}
          />
          <link
            rel="preload"
            as="image"
            href={`${media[0].url}?${templateNames.dvDesk}`}
            media={`(min-width: ${theme.breakpoints.values.md + 0.5}px)`}
          />
        </Head>
        <Box ref={parentRef} sx={{ height: '100%' }}>
          {/* transform: 'scale(1)' overrides the viewport for display: 'fixed' property of underlying ArrowButton component */}
          <Box sx={{ transform: 'scale(1)', width: '100%' }} ref={ref} position="sticky" top={0}>
            <Carousel
              index={selectedIndex}
              showArrows={!isMobile}
              thumbnailConfig={thumbnailConfig}
              aria={{
                buttonPrev: formatMessage(imageGalleryMessages.ariaPrevImage),
                buttonNext: formatMessage(imageGalleryMessages.ariaNextImage),
              }}
              onChange={(index) => setSelectedIndex(index)}
            >
              {media.map(({ url }, i) => (
                <Box
                  key={url}
                  sx={{
                    width: '100%',
                    height: 0,
                    paddingTop: '100%',
                    position: 'relative',
                    cursor: 'pointer',
                  }}
                >
                  {/* TODO(api-layer): add alt field to media type */}
                  <Image
                    loading={i !== 0 ? 'lazy' : 'eager'}
                    priority={i === 0}
                    src={`${url}?${galleryTemplateName}`}
                    alt={name}
                    style={{ objectFit: 'contain' }}
                    unoptimized
                    fill
                    onClick={handleImageClick}
                    data-testid={i}
                    displayTrackingProps={{
                      category: `dv-${i === 0 ? 'start' : i + 1}-product-image`,
                    }}
                  />
                </Box>
              ))}
            </Carousel>
            <ImageGalleryActionsWrapper isMobile={isMobile}>
              {mazingEnabled && mazingUrl && <Mazing data={product} />}
              <Cliplister data={product} />
              <ImageGalleryCallToActionButton
                onClick={handleImageClick}
                aria-label={formatMessage(imageGalleryMessages.zoom)}
              >
                <Enlarge32 sx={{ fill: theme.palette.text.dark }} />
              </ImageGalleryCallToActionButton>
              {isMobile && colorPicker}
            </ImageGalleryActionsWrapper>
            {dynamicYieldEnabled && (
              <>
                <DynamicYield dataDyId="dv_social-proof-inject" />
                <DynamicYield dataDyId="dv_social-proof" />
              </>
            )}
            <ImageGalleryFlags flags={additionalFlags} />
          </Box>
        </Box>
        {zoomDialogOpen && (
          <ErrorBoundary
            fallback={(reset) => (
              <ErrorFallback
                onClose={() => {
                  handleZoomDialogClose();
                  reset();
                }}
              />
            )}
          >
            <ZoomDialog
              data={product}
              carouselIndex={selectedIndex}
              open={zoomDialogOpen}
              close={handleZoomDialogClose}
            />
          </ErrorBoundary>
        )}
      </>
    );
  },
);
